import type * as React from 'react';
import type { TFunction } from 'i18next';

// ─── Shared types ────────────────────────────────────────────

export interface SelectedPhoto {
  id: string;
  file: File;
  url: string;
}

export interface ExistingPhoto {
  id: number;
  url: string;
  originalName: string;
  storageKey: string;
}

export interface NewPhoto {
  id: string;
  file: File;
  url: string;
}

export interface PhotoGalleryItem {
  id: string;
  url: string;
  name: string;
  isNew: boolean;
  onRemove: () => void;
  tileTestId: string;
  removeTestId: string;
}

export interface SharedPhotoUploadProps {
  onAddPhoto?: () => void;
  onReorderPhotos?: (fromId: string, toId: string) => void;
  galleryPhotos?: PhotoGalleryItem[];
}

// ─── Create mode props ───────────────────────────────────────

interface CreateModeProps {
  mode: 'create';
  photos: SelectedPhoto[];
  onFileChange: (files: FileList | File[]) => void;
  onRemovePhoto: (index: number) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  photoCountError: string | null;
  t: TFunction<'inventory'>;
}

// ─── Edit mode props ─────────────────────────────────────────

interface EditModeProps {
  mode: 'edit';
  existingPhotos: ExistingPhoto[];
  newPhotos: NewPhoto[];
  onFileChange: (files: FileList | File[]) => void;
  onRemoveExistingPhoto: (id: number) => void;
  onRemoveNewPhoto: (id: string) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  photoCountError: string | null;
  t: TFunction<'inventory'>;
}

export type PhotoUploadSectionProps =
  | (CreateModeProps & SharedPhotoUploadProps)
  | (EditModeProps & SharedPhotoUploadProps);

export function reorderGalleryPhotos(
  photos: PhotoGalleryItem[],
  fromId: string,
  toId: string,
): PhotoGalleryItem[] {
  const fromIndex = photos.findIndex((photo) => photo.id === fromId);
  const toIndex = photos.findIndex((photo) => photo.id === toId);

  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
    return photos;
  }

  const next = [...photos];
  const [moved] = next.splice(fromIndex, 1);

  if (!moved) {
    return photos;
  }

  next.splice(toIndex, 0, moved);
  return next;
}

export function buildGalleryPhotos(props: PhotoUploadSectionProps): PhotoGalleryItem[] {
  if (props.galleryPhotos) {
    return props.galleryPhotos;
  }

  if (props.mode === 'create') {
    return props.photos.map((photo, index) => ({
      id: photo.id,
      url: photo.url,
      name: photo.file.name,
      isNew: true,
      onRemove: () => props.onRemovePhoto(index),
      tileTestId: `new-photo-${photo.id}`,
      removeTestId: `photo-remove-${index}`,
    }));
  }

  return [
    ...props.existingPhotos.map((photo) => ({
      id: String(photo.id),
      url: photo.url,
      name: photo.originalName,
      isNew: false,
      onRemove: () => props.onRemoveExistingPhoto(photo.id),
      tileTestId: `existing-photo-${photo.id}`,
      removeTestId: `remove-existing-photo-${photo.id}`,
    })),
    ...props.newPhotos.map((photo) => ({
      id: photo.id,
      url: photo.url,
      name: photo.file.name,
      isNew: true,
      onRemove: () => props.onRemoveNewPhoto(photo.id),
      tileTestId: `new-photo-${photo.id}`,
      removeTestId: `remove-new-photo-${photo.id}`,
    })),
  ];
}
